import type { TooltipDefinition } from "./types";

const METRICS_DETAIL_HREF = "../metrics/";
const METRICS_DETAIL_LABEL = "Open Metrics In Detail";

export const TOOLTIP_DEFINITIONS: Record<string, TooltipDefinition> = {
  "dashboard-scope": {
    title: "Singapore Scope",
    body:
      "This dashboard covers Singapore port conditions. Provisional means the signal is still being validated against operating history and should be read as monitoring context.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "map-provenance": {
    title: "ArcGIS Operating Picture",
    body:
      "The map is an ArcGIS Web Map backed by secured ArcGIS Online Feature Services. It shows the latest vessel, terminal, anchorage, and berth-candidate context after sign-in.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "source-status": {
    title: "Source Status",
    body:
      "This badge reflects the current M10 score status. If required feeds are stale, the headline score is withheld even when diagnostic cards still show last-known values.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "congestion-signal": {
    title: "Congestion Signal",
    body:
      "M10 is a 0-100 monitoring signal from anchorage pressure, arrival surge, positive flow imbalance, and dwell pressure. It is provisional, Singapore-scoped, and not a pricing, payout, or operational trigger.",
    formula:
      "M10 = 0.30 x anchorage pressure index + 0.30 x arrival surge index + 0.20 x positive net-flow index + 0.20 x dwell pressure index.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "kpi-anchorage": {
    title: "Anchorage Pressure",
    body:
      "Counts slow or stationary vessels inside Singapore anchorage polygons. It is useful pressure context, not proof every vessel is waiting for a berth.",
    formula: "M1 = count(vessels inside anchorage polygons where speed <= 0.5 kn).",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "kpi-surge": {
    title: "Arrival Surge",
    body:
      "Counts inbound ETA records due soon. The due-to-arrive feed has ETA and port codes, but no inbound vessel coordinates.",
    formula: "M2 6h = count(inbound ETA records due within the next 6 hours).",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "kpi-dwell": {
    title: "Dwell P50",
    body:
      "Median matched arrival-to-departure duration over recent history. It needs continuous polling and still includes port-stay reasons beyond congestion.",
    formula: "M3 P50 = median(departure_ts - arrival_ts) over matched recent vessel stays.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "kpi-flow": {
    title: "Net Flow",
    body:
      "Arrival rate minus departure rate over the current window. Positive values indicate short-term accumulation pressure.",
    formula: "M6 = M4 - M5, where M4/M5 = rolling arrival/departure count / 6 hours.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "route-context": {
    title: "Route Delay Context",
    body:
      "M12-lite applies a Singapore route weight to the live M10 score for a fictional demo lane. It supports monitoring and commercial-review discussion only.",
    formula: "M12-lite = Singapore M10 x selected route weight.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "singapore-weight": {
    title: "Singapore Route Weight",
    body:
      "This what-if control changes how strongly the selected demo route depends on Singapore as a transshipment handoff. It stays in the browser and does not update ArcGIS.",
    formula: "Displayed exposure = Singapore M10 x selected Singapore route weight.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "top-driver": {
    title: "Top Driver",
    body:
      "The driver is the largest current M10 component contribution, such as arrival surge or anchorage pressure. It explains why the route exposure moved.",
    formula: "Top driver = max(queue, surge, dwell, accumulation component contribution).",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "terminal-occupancy": {
    title: "Terminal Occupancy",
    body:
      "Berth-adjacent occupancy counts slow cargo-like vessels near reviewed berth footprints or wharf-edge fallback geometry. It is diagnostic and excluded from M10.",
    formula: "M8 = berth-adjacent cargo-like vessels / source-based max berths x 100%, capped at 100%.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  },
  "scenario-link": {
    title: "Scenario Link",
    body:
      "The link stores route and slider values in the URL for roadshow handoff. It is not a saved preset and does not write to ArcGIS.",
    href: METRICS_DETAIL_HREF,
    linkLabel: METRICS_DETAIL_LABEL
  }
};
